// @flow
import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { TextInput } from '@coverright/ui/inputs';
import { NoData, Portal, useQaCompareQuery } from '../../../shared';
import { Preloader } from '@coverright/features';
import { ScoreText } from '../ui/qa-shared';
import { AgentMultiSelector } from '../../../features';

export const Compare = () => {
  const [labelA, setLabelA] = useState('Group A');
  const [labelB, setLabelB] = useState('Group B');
  const [agentsA, setAgentsA] = useState<string[]>([]);
  const [agentsB, setAgentsB] = useState<string[]>([]);

  const ready = agentsA.length > 0 && agentsB.length > 0;
  const { data, loading } = useQaCompareQuery({
    variables: { groups: [{ label: labelA, agentIds: agentsA }, { label: labelB, agentIds: agentsB }] },
    skip: !ready,
    fetchPolicy: 'cache-and-network',
  });

  const groups = data?.qaCompare.groups || [];
  const comparison = data?.qaCompare.comparison;

  return (
    <Stack gap={3}>
      <Stack direction={'row'} gap={3} flexWrap={'wrap'}>
        <Portal p={2} flex={1} minWidth={320}>
          <Typography variant={'overline'} color={'text.secondary'}>Group A (baseline)</Typography>
          <TextInput hideTick fullWidth value={labelA} onChange={(e: any) => setLabelA(e.target.value)} wrapperSx={{ mb: 2 }} />
          <AgentMultiSelector label={'Agents'} value={agentsA} onChange={setAgentsA} />
        </Portal>
        <Portal p={2} flex={1} minWidth={320} sx={{ borderColor: '#7C3AED' }}>
          <Typography variant={'overline'} color={'text.secondary'}>Group B</Typography>
          <TextInput hideTick fullWidth value={labelB} onChange={(e: any) => setLabelB(e.target.value)} wrapperSx={{ mb: 2 }} />
          <AgentMultiSelector label={'Agents'} value={agentsB} onChange={setAgentsB} />
        </Portal>
      </Stack>

      {!ready && <NoData>Pick at least one agent for each group to compare.</NoData>}
      {ready && loading && !data && <Preloader sx={{ height: 200 }} />}

      {ready && comparison && (
        <>
          <Stack direction={'row'} gap={3} flexWrap={'wrap'}>
            <Portal p={2} flex={1} minWidth={280}>
              <Typography variant={'caption'} color={'text.secondary'}>{groups[0]?.label}</Typography>
              <ScoreText score={groups[0]?.report.summary.averageScore} variant={'h2'} />
              <Typography variant={'caption'} color={'text.secondary'}>Avg across {agentsA.length} agents</Typography>
            </Portal>
            <Portal p={2} flex={1} minWidth={280}>
              <Typography variant={'caption'} color={'text.secondary'}>{groups[1]?.label}</Typography>
              <Stack direction={'row'} alignItems={'center'} gap={1}>
                <ScoreText score={groups[1]?.report.summary.averageScore} variant={'h2'} />
                <Typography variant={'subtitle2'} sx={{ color: comparison.scoreDelta >= 0 ? '#0DBC83' : '#F14E42' }}>
                  {comparison.scoreDelta >= 0 ? '+' : ''}{Math.round(comparison.scoreDelta)} pts vs A
                </Typography>
              </Stack>
              <Typography variant={'caption'} color={'text.secondary'}>Avg across {agentsB.length} agents</Typography>
            </Portal>
          </Stack>

          <Portal p={2}>
            <Typography variant={'subtitle1'} mb={2}>By category</Typography>
            <Stack direction={'row'} sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
              <Typography variant={'caption'} color={'text.secondary'} flex={1}>Category</Typography>
              <Typography variant={'caption'} color={'text.secondary'} width={90} textAlign={'right'}>Group A</Typography>
              <Typography variant={'caption'} color={'text.secondary'} width={90} textAlign={'right'}>Group B</Typography>
              <Typography variant={'caption'} color={'text.secondary'} width={90} textAlign={'right'}>Delta</Typography>
            </Stack>
            {comparison.categoryDeltas.map(c => (
              <Stack key={c.category} direction={'row'} alignItems={'center'} sx={{ py: 1 }}>
                <Typography variant={'body2'} flex={1}>{c.category}</Typography>
                <Typography variant={'body2'} width={90} textAlign={'right'}>{c.groupA}%</Typography>
                <Typography variant={'body2'} width={90} textAlign={'right'}>{c.groupB}%</Typography>
                <Typography variant={'body2'} width={90} textAlign={'right'}
                            sx={{ color: c.delta >= 0 ? '#0DBC83' : '#F14E42', fontWeight: 600 }}>
                  {c.delta >= 0 ? '+' : ''}{c.delta}
                </Typography>
              </Stack>
            ))}
          </Portal>
        </>
      )}
    </Stack>
  );
};